/**
 * ================ API error handling utilities ================
 *
 * This module exports helpers that can be used to check if API
 * response errors are some specific error cases.
 *
 * NOTE: most of the functions are tied to an endpoint, and should not
 * be used to check error responses from any other endpoint. Check the
 * name and the docstring of the function to ensure correct usage.
 */

import {
  ERROR_CODE_FORBIDDEN,
  ERROR_CODE_NOT_FOUND,
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE,
  ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK,
  ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH,
  ERROR_CODE_PERMISSION_DENIED_POST_LISTINGS,
  ERROR_CODE_PERMISSION_DENIED_PENDING_APPROVAL,
  ERROR_CODE_USER_PENDING_APPROVAL,
  ERROR_CODE_PERMISSION_DENIED_INITIATE_TRANSACTIONS,
  ERROR_CODE_PERMISSION_DENIED_READ,
  ERROR_CODE_LISTING_PRIVATE,
  ERROR_CODE_LISTING_VACATION_MODE,
  ERROR_CODE_IMAGE_INVALID_CONTENT,
} from './types';
import {
  SIGNUP_EMAIL_ERROR_SUSPICIOUS,
  SIGNUP_NAME_ERROR_RANDOM,
  SIGNUP_NAME_ERROR_RESERVED,
} from './signupNameValidation';
import { SIGNUP_CAPTCHA_ERROR_FAILED, SIGNUP_CAPTCHA_ERROR_REQUIRED } from './captcha';
import { SIGNUP_RATE_LIMIT_ERROR_CODE } from './signupRateLimit';
import { StatusCode } from './enums';
// NOTE: This file imports types.js, which may lead to circular dependency

const errorAPIErrors = error => {
  const apiErrors = error && error.apiErrors ? error.apiErrors : [];
  return Array.isArray(apiErrors) ? apiErrors : [];
};

const hasErrorWithCode = (error, code) => {
  const apiErrors = errorAPIErrors(error);
  const responseErrors = responseAPIErrors(error);
  return [...apiErrors, ...responseErrors].some(apiError => {
    return apiError.code === code;
  });
};

/**
 * return apiErrors from error response
 */
const responseAPIErrors = error => {
  const errors = error && error.data && error.data.errors ? error.data.errors : [];
  return Array.isArray(errors) ? errors : [];
};

/**
 * Returns true for transient network failures where no response was received.
 * Covers Chrome ('Network Error', 'Failed to fetch'), Safari ('Load failed'), and Axios ERR_NETWORK.
 * Also includes aborted requests (ERR_CANCELED) which are typically due to user navigation.
 * Also includes request timeouts (Axios ECONNABORTED / 'timeout exceeded').
 * Also covers WebKit DOMException JSON parse failures (malformed/empty body from proxies/CDN)
 * and body-stream-already-read TypeErrors (browser extension or service worker consumed the body).
 * Note: 502/503/504 are NOT treated as network errors — they are real HTTP responses that should
 * be visible in Rollbar as warnings so spikes can be detected.
 * Pass requireNoResponse=false to skip the response-presence check (e.g. Axios-free contexts).
 */
export const isNetworkError = (error, { requireNoResponse = true } = {}) => {
  const noResponse = !requireNoResponse || !error?.response;
  const message = error?.message || '';
  const isNetworkMessage =
    /network error/i.test(message) ||
    /load failed/i.test(message) ||
    /failed to fetch/i.test(message) ||
    /request aborted/i.test(message) ||
    /timeout/i.test(message) ||
    /loading (?:css )?chunk \d+ failed/i.test(message);

  const isAxiosError = error?.name === 'AxiosError' || error?.isAxiosError === true;

  // WebKit/Safari throws DOMException instead of SyntaxError when res.json() receives a
  // non-JSON body (e.g. HTML error page from a CDN or proxy returning 200 with wrong body).
  const isSyntaxError = error instanceof SyntaxError || error?.name === 'SyntaxError';
  const isWebKitJsonParseFailure =
    error?.parseError === true ||
    (error?.constructor?.name === 'DOMException' && /the string did not match/i.test(message)) ||
    (isSyntaxError && (/unexpected (end|eof)/i.test(message) || /JSON parse error/i.test(message)));

  // Browser extensions, service workers, or fetch interceptors can consume the response body
  // stream before our code reads it, causing res.json()/res.text() to throw this TypeError.
  const isBodyStreamAlreadyRead = /body stream already read/i.test(message);

  return (
    (noResponse &&
      (isNetworkMessage ||
        isAxiosError ||
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ERR_CANCELED' ||
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ETIMEDOUT')) ||
    isWebKitJsonParseFailure ||
    isBodyStreamAlreadyRead
  );
};

/**
 * 403 Forbidden
 */
export const isForbiddenError = error => {
  const status = error?.status || error?.response?.status;
  return status === StatusCode.FORBIDDEN || hasErrorWithCode(error, ERROR_CODE_FORBIDDEN);
};

/**
 * 404 Not Found
 */
export const isNotFoundError = error => {
  const status = error?.status || error?.response?.status;
  return status === StatusCode.NOT_FOUND || hasErrorWithCode(error, ERROR_CODE_NOT_FOUND);
};

/**
 * 429 Too Many Requests error
 */
export const isTooManyRequestsError = error => {
  const status = error?.status || error?.response?.status;
  return status === StatusCode.TOO_MANY_REQUESTS;
};

/**
 * Check if the given API error (from `sdk.currentuser.create()`) is
 * due to the email address already being in use.
 */
export const isSignupEmailTakenError = error => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if signup failed due to reserved/disallowed name content.
 */
export const isSignupNameReservedError = error =>
  hasErrorWithCode(error, SIGNUP_NAME_ERROR_RESERVED);

/**
 * Check if signup failed due to random/suspicious name pattern.
 */
export const isSignupNameRandomError = error => hasErrorWithCode(error, SIGNUP_NAME_ERROR_RANDOM);

/**
 * Check if signup failed due to a suspicious disposable/bot-like email pattern.
 */
export const isSignupEmailSuspiciousError = error =>
  hasErrorWithCode(error, SIGNUP_EMAIL_ERROR_SUSPICIOUS);

/**
 * Check if signup failed due to missing captcha challenge.
 */
export const isSignupCaptchaRequiredError = error =>
  hasErrorWithCode(error, SIGNUP_CAPTCHA_ERROR_REQUIRED);

/**
 * Check if signup failed due to failed captcha verification.
 */
export const isSignupCaptchaFailedError = error =>
  hasErrorWithCode(error, SIGNUP_CAPTCHA_ERROR_FAILED);

/**
 * Check if signup failed because local account-creation rate limiting blocked it.
 */
export const isSignupRateLimitedError = error =>
  hasErrorWithCode(error, SIGNUP_RATE_LIMIT_ERROR_CODE);

/**
 * Check if the given API error (from `sdk.currentuser.changeEmail()`) is
 * due to the email address already being in use.
 */
export const isChangeEmailTakenError = error => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if the given API error (from
 * `sdk.currentUser.sendVerificationEmail()`) is due to too many
 * active email verification requests.
 *
 * There qre only a specific amount of active verification requests
 * allowed, and the user has to wait for them to expire to be able to
 * request sending new verification emails.
 */
export const isTooManyEmailVerificationRequestsError = error =>
  hasErrorWithCode(error, ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS);

/**
 * Check if the given API error (from
 * `sdk.images.upload()`) is due to the image being over
 * the size limit.
 */
export const isUploadImageOverLimitError = error =>
  hasErrorWithCode(error, ERROR_CODE_UPLOAD_OVER_LIMIT);

/**
 * Check if the given API error (from
 * `sdk.images.upload()`) is due to the image content being invalid (e.g. empty file).
 */
export const isImageInvalidContentError = error =>
  hasErrorWithCode(error, ERROR_CODE_IMAGE_INVALID_CONTENT);

/**
 * Check if the given API error (from
 * `sdk.stock.compareAndSet()`) is due to the oldTotal being wrong.
 */
export const isOldTotalMismatchStockError = error =>
  hasErrorWithCode(error, ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH);

/**
 * Check if the given API error (from `sdk.passwordReset.request()`)
 * is due to no user having the given email address.
 */
export const isPasswordRecoveryEmailNotFoundError = error =>
  hasErrorWithCode(error, ERROR_CODE_EMAIL_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to the listing
 * being closed or deleted.
 */
export const isTransactionInitiateListingNotFoundError = error =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to missign Stripe
 * connection from the listing author.
 */
export const isTransactionInitiateMissingStripeAccountError = error =>
  hasErrorWithCode(error, ERROR_CODE_MISSING_STRIPE_ACCOUNT);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to selected booking
 * time already being booked.
 */
export const isTransactionInitiateBookingTimeNotAvailableError = error =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to insufficient stock.
 */
export const isTransactionInitiateListingInsufficientStockError = error =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to payment being zero.
 */
export const isTransactionZeroPaymentError = error =>
  hasErrorWithCode(error, ERROR_CODE_CHARGE_ZERO_PAYIN);

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction total amount being too low for Stripe.
 */
export const isTransactionInitiateAmountTooLowError = error => {
  const isZeroPayment = isTransactionZeroPaymentError(error);

  const tooLowAmount = errorAPIErrors(error).some(apiError => {
    const isPaymentFailedError =
      apiError.status === StatusCode.PAYMENT_REQUIRED &&
      apiError.code === ERROR_CODE_PAYMENT_FAILED;
    let isAmountTooLow = false;

    try {
      // TODO: This is a temporary solution until a proper error code
      // for this specific error is received in the response.
      const msg = apiError.meta.stripeMessage;
      isAmountTooLow =
        msg.startsWith('Amount must be at least') ||
        msg.startsWith('Amount must convert to at least');
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isAmountTooLow;
  });

  return isZeroPayment || tooLowAmount;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction charge creation disabled by Stripe.
 */
export const isTransactionChargeDisabledError = error => {
  const chargeCreationDisabled = errorAPIErrors(error).some(apiError => {
    const isPaymentFailedError =
      apiError.status === StatusCode.PAYMENT_REQUIRED &&
      apiError.code === ERROR_CODE_PAYMENT_FAILED;

    let isChargeCreationDisabled = false;
    try {
      const msg = apiError.meta.stripeMessage;
      isChargeCreationDisabled =
        msg.startsWith('Your account cannot currently make charges.') ||
        msg.match(/verification.disabled_reason/);
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isChargeCreationDisabled;
  });

  return chargeCreationDisabled;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to other error in Stripe.
 */
export const transactionInitiateOrderStripeErrors = error => {
  if (error) {
    return errorAPIErrors(error).reduce((messages, apiError) => {
      const isPaymentFailedError =
        apiError.status === StatusCode.PAYMENT_REQUIRED &&
        apiError.code === ERROR_CODE_PAYMENT_FAILED;
      const hasStripeError = apiError && apiError.meta && apiError.meta.stripeMessage;
      const stripeMessageMaybe =
        isPaymentFailedError && hasStripeError ? [apiError.meta.stripeMessage] : [];
      return [...messages, ...stripeMessageMaybe];
    }, []);
  }
  return null;
};

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to invalid transition attempt.
 */
export const isTransactionsTransitionInvalidTransition = error =>
  error &&
  error.status === StatusCode.CONFLICT &&
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_INVALID_TRANSITION);

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to already sent review.
 */
export const isTransactionsTransitionAlreadyReviewed = error =>
  error &&
  error.status === StatusCode.CONFLICT &&
  (hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER) ||
    hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER));

/**
 * Check if the given API error (from `sdk.currentUser.changeEmail(params)`)
 * is due to giving wrong password.
 */
export const isChangeEmailWrongPassword = error => error && error.status === StatusCode.FORBIDDEN;

/**
 * Check if the given API error (from `sdk.currentUser.changePassword(params)`)
 * is due to giving wrong password.
 */
export const isChangePasswordWrongPassword = error =>
  error && error.status === StatusCode.FORBIDDEN;

/**
 * Check if the given API error (from `sdk.listings.open(params)` or `sdk.listings.publish(params)`)
 * is due to denied permission to post listings.
 */
export const isErrorNoPermissionToPostListings = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_PERMISSION_DENIED_POST_LISTINGS);

/**
 * Check if the given API error (from `sdk.transactions.initiate(params)`
 * is due to denied permission for users in pending-approval state.
 */
export const isErrorNoPermissionForUserPendingApproval = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_PERMISSION_DENIED_PENDING_APPROVAL);

/**
 * Check if the given API error (from `sdk.listings.query(params)`
 * is due to denied permission for users in pending-approval state.
 */
export const isErrorUserPendingApproval = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_USER_PENDING_APPROVAL);

/**
 * Check if the given API error (from `sdk.transactions.initiate(params)`
 * is due to denied permission to initiate transactions.
 */
export const isErrorNoPermissionForInitiateTransactions = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_PERMISSION_DENIED_INITIATE_TRANSACTIONS);

/**
 * Check if local API rejected checkout because the listing is private.
 */
export const isListingPrivateError = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_LISTING_PRIVATE);

/**
 * Check if local API rejected checkout because the listing is in vacation mode.
 */
export const isListingVacationModeError = error =>
  error &&
  error.status === StatusCode.FORBIDDEN &&
  hasErrorWithCode(error, ERROR_CODE_LISTING_VACATION_MODE);

export const getShippingDestinationErrorId = error => {
  if (!error) return null;
  if (error.code === 'SHOP_US_ONLY_SHIPPING' || hasErrorWithCode(error, 'SHOP_US_ONLY_SHIPPING')) {
    return 'CheckoutPage.usOnlyShipping';
  }
  if (error.code === 'SHIPPING_COUNTRY_REQUIRED' || hasErrorWithCode(error, 'SHIPPING_COUNTRY_REQUIRED')) {
    return 'CheckoutPage.shippingCountryRequired';
  }
  return null;
};

/** The server refused a purchase because no shipping was chosen (server/api-util/shippingRequired.js). */
export const isShippingNotSelectedError = error =>
  error?.statusText === 'shipping-not-selected' ||
  error?.apiErrors?.some?.(e => e?.code === 'shipping-not-selected') ||
  false;

export const getShippingDestinationErrorMessage = (error, intl) => {
  const id = getShippingDestinationErrorId(error);
  return id ? intl.formatMessage({ id }) : null;
};

/**
 * Check if the given API error (from `sdk.transactions.initiate(params)`
 * is due to denied permission to view marketplace data.
 */
export const isErrorNoViewingPermission = error => {
  return (
    error &&
    error.status === StatusCode.FORBIDDEN &&
    hasErrorWithCode(error, ERROR_CODE_PERMISSION_DENIED_READ)
  );
};

/**
 * Check if the given API error (from
 * 'sdk.stripeAccount.create(payoutDetails)') is due to
 * invalid postal code in the given country.
 */
export const isStripeInvalidPostalCode = error => {
  const msgRe = /^Invalid [A-Z]{2} postal code$/;
  return errorAPIErrors(error).some(apiError => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    const msg = apiError.meta && apiError.meta.stripeMessage ? apiError.meta.stripeMessage : '';
    return msgRe.test(msg);
  });
};

export const isStripeError = error => {
  return errorAPIErrors(error).some(apiError => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    return !!(apiError.meta && apiError.meta.stripeMessage);
  });
};

const STRIPE_CUSTOMER_ACTIONABLE_ERROR_TYPES = new Set(['card_error', 'validation_error']);
const STRIPE_ERROR_TYPES = new Set([
  'api_connection_error',
  'api_error',
  'authentication_error',
  'card_error',
  'idempotency_error',
  'invalid_request_error',
  'rate_limit_error',
  'validation_error',
]);
const STRIPE_INCOMPLETE_INPUT_MESSAGE_RE = /^Your .* is incomplete\.$/i;

const getStripeIntentDetails = error => {
  const stripeError = error?.error || error;
  const paymentIntent = stripeError?.payment_intent || stripeError?.paymentIntent || null;
  const setupIntent = stripeError?.setup_intent || stripeError?.setupIntent || null;

  return {
    paymentIntentId: paymentIntent?.id || null,
    paymentIntentStatus: paymentIntent?.status || null,
    setupIntentId: setupIntent?.id || null,
    setupIntentStatus: setupIntent?.status || null,
  };
};

export const getStripeClientErrorDetails = error => {
  const stripeError = error?.error || error;
  if (!stripeError || typeof stripeError !== 'object') {
    return null;
  }

  const hasStripeFields =
    STRIPE_ERROR_TYPES.has(stripeError?.type) ||
    ['code', 'decline_code', 'doc_url', 'payment_intent', 'setup_intent'].some(
      key => typeof stripeError?.[key] !== 'undefined'
    );

  if (!hasStripeFields && !STRIPE_INCOMPLETE_INPUT_MESSAGE_RE.test(stripeError?.message || '')) {
    return null;
  }

  return {
    type: stripeError?.type || null,
    code: stripeError?.code || null,
    declineCode: stripeError?.decline_code || null,
    param: stripeError?.param || null,
    docUrl: stripeError?.doc_url || stripeError?.docUrl || null,
    message: stripeError?.message || null,
    ...getStripeIntentDetails(stripeError),
  };
};

export const isStripeCustomerActionableError = error => {
  const stripeErrorDetails = getStripeClientErrorDetails(error);
  if (!stripeErrorDetails) {
    return false;
  }

  return (
    STRIPE_CUSTOMER_ACTIONABLE_ERROR_TYPES.has(stripeErrorDetails.type) ||
    STRIPE_INCOMPLETE_INPUT_MESSAGE_RE.test(stripeErrorDetails.message || '')
  );
};

/**
 * Check if the given transition error is
 * due to no quantity information in the transition params.
 */
export const isTransitionQuantityInfoMissingError = error =>
  error?.status === StatusCode.BAD_REQUEST &&
  error?.statusText.startsWith('Error: transition should contain quantity information');

/**
 * Check if the minimum provider commission is larger than the
 * minimum price set for the listing.
 */
export const isProviderCommissionBiggerThanMinPrice = error =>
  error?.status === StatusCode.BAD_REQUEST &&
  error?.statusText.startsWith(
    'Minimum commission amount is greater than the amount of money paid in'
  );

/**
 * Check if the user has any unfinished transactions that include Stripe payment
 * processing actions.
 */
export const isErrorUserHasUnfinishedTransactions = error => {
  return (
    error?.status === StatusCode.CONFLICT &&
    error?.statusText.startsWith(
      'User has transactions on states that include incomplete payment processing'
    )
  );
};

/**
 * Check if the request failed because Stripe prevented account deletion. This happens if the
 * user's Stripe Connect account as a non-zero balance. See the [API reference]() for details.
 */
export const isStripeDeletionFailedNonZeroBalance = error => {
  return (
    error?.status === StatusCode.BAD_REQUEST &&
    errorAPIErrors(error).some(apiError => apiError.code === 'delete-stripe-account-failed')
  );
};

export const storableError = err => {
  const error = err || {};
  const { name, message, status, statusText, code } = error;
  // Status, statusText, and data.errors are (possibly) added to the error object by SDK
  const apiErrors = responseAPIErrors(error);

  // Returned object is the same as prop type check in util/types -> error
  return {
    type: 'error',
    name: name || 'Error',
    code,
    message: message || statusText || (status ? `HTTP ${status}` : 'Unknown error'),
    status,
    statusText,
    apiErrors,
    // Shipping recovery needs the server's safe action/field errors after Redux
    // serialization; otherwise the order page falls back to generic address UI.
    ...(error.details?.action ? { details: error.details } : {}),
  };
};
